import axios from 'axios'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_URL } from "../../../config";
import axiosInstance from '../../../utils/axiosInstance'

function AddServices() {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [formData, setFormData] = useState({
    deptId: "",
    serviceName: "",
    servicePrice: "",
    is_recurring: false,
    subscriber: "",
    plan: "",
    billingCycle: "",
    paymentMethod: "",
    createdDate: new Date().toISOString().split("T")[0],
    expiringOn: "",
    billToAddress: "",
    startDate: "",
    duration: 1,
    durationUnit: "months",
    renewalType: "monthly",
    reminderDaysBefore: 3,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/getDepartment`);
        setDepartments(res.data);
      } catch (error) {
        console.error("Error fetching departments:", error);
      }
    };
    fetchDepartments();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "is_recurring") {
      setFormData((prev) => ({
        ...prev,
        is_recurring: checked,
      }));
      setShowSubscriptionModal(checked);
      setErrors((prev) => ({ ...prev, is_recurring: "" }));
      return;
    }

    if (name === "durationUnit") {
      setFormData((prev) => ({
        ...prev,
        durationUnit: value,
        renewalType: value === "years" ? "yearly" : "monthly",
      }));
      setErrors((prev) => ({ ...prev, durationUnit: "", renewalType: "" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateAll = () => {
    const newErrors = {};
    if (!formData.deptId) newErrors.deptId = "Department is required.";
    if (!formData.serviceName.trim()) newErrors.serviceName = "Service name is required.";
    if (!formData.servicePrice) newErrors.servicePrice = "Service price is required.";
    else if (isNaN(formData.servicePrice) || parseFloat(formData.servicePrice) <= 0)
      newErrors.servicePrice = "Service price must be a positive number.";

    if (formData.is_recurring) {
      if (!formData.subscriber.trim()) newErrors.subscriber = "Subscriber is required.";
      if (!formData.plan.trim()) newErrors.plan = "Plan name is required.";
      if (!formData.billingCycle.trim()) newErrors.billingCycle = "Billing cycle is required.";
      if (!formData.paymentMethod) newErrors.paymentMethod = "Payment method is required.";
      if (!formData.createdDate) newErrors.createdDate = "Created date is required.";
      if (!formData.expiringOn) newErrors.expiringOn = "Expiring date is required.";
      if (!formData.startDate) newErrors.startDate = "Start date is required.";
      if (!formData.duration || Number(formData.duration) <= 0) newErrors.duration = "Duration must be a positive number.";
      if (!formData.renewalType) newErrors.renewalType = "Renewal type is required.";
      if (Number(formData.reminderDaysBefore) < 0) newErrors.reminderDaysBefore = "Reminder days cannot be negative.";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validatedErrors = validateAll();
    setErrors(validatedErrors);

    if (Object.keys(validatedErrors).length > 0) {
      if (formData.is_recurring) setShowSubscriptionModal(true);
      return;
    }

    try {
      const payload = {
        ...formData,
        renewalType: formData.renewalType,
      };
      const res = await axiosInstance.post('/api/addService', payload);

      if (res.data.success) {
        if (formData.is_recurring && !res.data.subscriptionDashboardSaved) {
          await axiosInstance.post('/api/subscriptions', {
            subscriber: formData.subscriber.trim(),
            plan: formData.plan.trim(),
            billingCycle: formData.billingCycle.trim(),
            paymentMethod: formData.paymentMethod.trim(),
            billToAddress: formData.billToAddress.trim(),
            amount: Number(formData.servicePrice),
            createdDate: formData.createdDate,
            expiringOn: formData.expiringOn,
          });
        }

        alert(res.data.message || "Service added successfully");
        setFormData({ 
          deptId: "", 
          serviceName: "", 
          servicePrice: "",
          is_recurring: false,
          subscriber: "",
          plan: "",
          billingCycle: "",
          paymentMethod: "",
          createdDate: new Date().toISOString().split("T")[0],
          expiringOn: "",
          billToAddress: "",
          startDate: "",
          duration: 1,
          durationUnit: "months",
          renewalType: "monthly",
          reminderDaysBefore: 3,
        });
        navigate("/admin/service");
      } else {
        alert(res.data.message || "Something went wrong");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to submit form");
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#f8fafc",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px"
    }}>

      <div style={{
        width: "100%",
        maxWidth: "500px",
        display: "flex",
        justifyContent: "flex-start",
        marginBottom: "15px"
      }}>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      <div style={{
        width: "100%",
        maxWidth: "500px",
        background: "#ffffff",
        backdropFilter: "blur(15px)",
        borderRadius: "20px",
        boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
        overflow: "hidden"
      }}>

        {/* HEADER */}
        <div style={{
          padding: "20px",
          textAlign: "center",
          background: "linear-gradient(90deg,#003e6d 0%,#0b4f84 50%,#1f5f95 100%)",
          color: "#fff"
        }}>
          <h3 style={{ margin: 0, fontWeight: "600" }}>
            Add New Service
          </h3>
        </div>

        {/* FORM */}
        <div style={{ padding: "25px" }}>
          <form id="add-service-form" onSubmit={handleSubmit}>

            {/* Service Name */}
            <div style={{ marginBottom: "18px" }}>
              <label style={label}>Service Name</label>
              <input
                type="text"
                name="serviceName"
                value={formData.serviceName}
                onChange={handleChange}
                placeholder="Enter service name"
                style={input}
              />
              {errors.serviceName && <div style={error}>{errors.serviceName}</div>}
            </div>

            {/* Price */}
            <div style={{ marginBottom: "18px" }}>
              <label style={label}>Service Price (INR)</label>
              <input
                type="number"
                name="servicePrice"
                value={formData.servicePrice}
                onChange={handleChange}
                placeholder="Enter price"
                style={input}
              />
              {errors.servicePrice && <div style={error}>{errors.servicePrice}</div>}
            </div>

            {/* Department */}
            <div style={{ marginBottom: "22px" }}>
              <label style={label}>Select Department</label>
              <select
                name="deptId"
                value={formData.deptId}
                onChange={handleChange}
                style={input}
              >
                <option value="">-- Select Department --</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.deptName}
                  </option>
                ))}
              </select>
              {errors.deptId && <div style={error}>{errors.deptId}</div>}
            </div>

            {/* Recurring Service Checkbox */}
            <div style={{ marginBottom: "18px", display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="checkbox"
                name="is_recurring"
                id="is_recurring"
                checked={formData.is_recurring}
                onChange={handleChange}
                style={{ width: "18px", height: "18px", cursor: "pointer" }}
              />
              <label htmlFor="is_recurring" style={{ ...label, marginBottom: 0, cursor: "pointer" }}>
                Is Recurring Service?
              </label>
            </div>

            {formData.is_recurring && (
              <button
                type="button"
                style={subscriptionOpenBtn}
                onClick={() => setShowSubscriptionModal(true)}
              >
                Open Subscription Form
              </button>
            )}

            {/* BUTTON */}
            <button type="submit" style={button}>
              Add Service
            </button>

          </form>
        </div>

      </div>

      {formData.is_recurring && showSubscriptionModal && (
        <div style={subscriptionModalOverlay} onClick={() => setShowSubscriptionModal(false)}>
          <div style={subscriptionModal} onClick={(e) => e.stopPropagation()}>
            <h3 style={subscriptionTitle}>Add New Subscription</h3>

            <div style={subscriptionGrid}>
              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>SUBSCRIBER</label>
                <input
                  type="text"
                  name="subscriber"
                  value={formData.subscriber}
                  onChange={handleChange}
                  placeholder="e.g. Client name"
                  style={subscriptionInput}
                />
                {errors.subscriber && <div style={error}>{errors.subscriber}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>PLAN NAME</label>
                <input
                  type="text"
                  name="plan"
                  value={formData.plan}
                  onChange={handleChange}
                  placeholder="e.g. Enterprise"
                  style={subscriptionInput}
                />
                {errors.plan && <div style={error}>{errors.plan}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>BILLING CYCLE</label>
                <input
                  type="text"
                  name="billingCycle"
                  value={formData.billingCycle}
                  onChange={handleChange}
                  placeholder="e.g. 30 days"
                  style={subscriptionInput}
                />
                {errors.billingCycle && <div style={error}>{errors.billingCycle}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>PAYMENT METHOD</label>
                <select
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  style={subscriptionInput}
                >
                  <option value="" disabled>Select Payment Method</option>
                  <option value="UPI">UPI</option>
                  <option value="Net Banking">Net Banking</option>
                  <option value="Debit Card">Debit Card</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Cash">Cash</option>
                </select>
                {errors.paymentMethod && <div style={error}>{errors.paymentMethod}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>AMOUNT</label>
                <input
                  type="number"
                  value={formData.servicePrice}
                  placeholder="e.g. 9000"
                  style={subscriptionInput}
                  readOnly
                />
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>CREATED ON</label>
                <input
                  type="date"
                  name="createdDate"
                  value={formData.createdDate}
                  onChange={handleChange}
                  style={subscriptionInput}
                />
                {errors.createdDate && <div style={error}>{errors.createdDate}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>EXPIRING ON</label>
                <input
                  type="date"
                  name="expiringOn"
                  value={formData.expiringOn}
                  onChange={handleChange}
                  style={subscriptionInput}
                />
                {errors.expiringOn && <div style={error}>{errors.expiringOn}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>BILL TO ADDRESS</label>
                <input
                  type="text"
                  name="billToAddress"
                  value={formData.billToAddress}
                  onChange={handleChange}
                  placeholder="Optional"
                  style={subscriptionInput}
                />
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>SERVICE NAME (FOR SUBSCRIPTION)</label>
                <input type="text" value={formData.serviceName} style={subscriptionInput} readOnly />
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>AMOUNT (FOR SUBSCRIPTION)</label>
                <input type="number" value={formData.servicePrice} style={subscriptionInput} readOnly />
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>START DATE</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  style={subscriptionInput}
                />
                {errors.startDate && <div style={error}>{errors.startDate}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>DURATION</label>
                <div style={durationRow}>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    style={{ ...subscriptionInput, marginBottom: 0 }}
                  />
                  <select
                    name="durationUnit"
                    value={formData.durationUnit}
                    onChange={handleChange}
                    style={{ ...subscriptionInput, marginBottom: 0 }}
                  >
                    <option value="months">Months</option>
                    <option value="years">Years</option>
                  </select>
                </div>
                {errors.duration && <div style={error}>{errors.duration}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>RENEWAL TYPE</label>
                <select
                  name="renewalType"
                  value={formData.renewalType}
                  onChange={handleChange}
                  style={subscriptionInput}
                >
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
                {errors.renewalType && <div style={error}>{errors.renewalType}</div>}
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label style={subscriptionModalLabel}>REMINDER DAYS BEFORE RENEWAL</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  name="reminderDaysBefore"
                  value={formData.reminderDaysBefore}
                  onChange={handleChange}
                  style={subscriptionInput}
                />
                {errors.reminderDaysBefore && <div style={error}>{errors.reminderDaysBefore}</div>}
              </div>
            </div>

            <div style={subscriptionModalActions}>
              <button
                type="button"
                style={subscriptionCancelBtn}
                onClick={() => {
                  setFormData((prev) => ({ ...prev, is_recurring: false }));
                  setShowSubscriptionModal(false);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={subscriptionSaveBtn}
                form="add-service-form"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddServices;

// ===== STYLES =====

const label = {
  display: "block",
  marginBottom: "6px",
  color: "#000000",
  fontSize: "14px"
}

const input = {
  width: "100%",
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid #334155",
  background: "#ffffff",
  color: "#000000",
  outline: "none",
  transition: "0.3s"
}

const button = {
  width: "100%",
  padding: "12px",
  borderRadius: "30px",
  border: "none",
  background: "linear-gradient(105deg, #c9d9ea, #c0d2e4 60%, #b5c9de)",
  color: "#0b4f84",
  fontWeight: "600",
  fontSize: "15px",
  boxShadow: "0 0 15px rgba(11,79,132,0.45)",
  transition: "0.3s",
  cursor: "pointer"
}

const error = {
  color: "#f87171",
  fontSize: "12px",
  marginTop: "5px"
}

const subscriptionWrap = {
  padding: "22px",
  background: "#f8fafc",
  borderRadius: "14px",
  marginBottom: "20px",
  border: "1px solid #dbe4ef"
}

const subscriptionTitle = {
  margin: "0 0 18px 0",
  fontWeight: "700",
  color: "#0f172a"
}

const subscriptionGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "0 14px"
}

const subscriptionOpenBtn = {
  width: "100%",
  marginBottom: "20px",
  padding: "10px 14px",
  borderRadius: "10px",
  border: "1px solid #c4d3e4",
  background: "#f8fbff",
  color: "#0b4f84",
  fontWeight: "600",
  cursor: "pointer"
}

const subscriptionModalOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(8, 36, 65, 0.55)",
  backdropFilter: "blur(3px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  zIndex: 1200
}

const subscriptionModal = {
  width: "100%",
  maxWidth: "780px",
  maxHeight: "88vh",
  overflowY: "auto",
  background: "#ffffff",
  borderRadius: "16px",
  padding: "24px",
  boxShadow: "0 20px 50px rgba(3, 27, 49, 0.35)"
}

const subscriptionModalLabel = {
  display: "block",
  marginBottom: "6px",
  color: "#64748b",
  fontWeight: "600",
  fontSize: "13px"
}

const subscriptionInput = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: "10px",
  border: "1px solid #b8c7d8",
  background: "#ffffff",
  color: "#0f172a",
  outline: "none"
}

const durationRow = {
  display: "grid",
  gridTemplateColumns: "1fr 130px",
  gap: "8px",
}

const subscriptionModalActions = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px"
}

const subscriptionCancelBtn = {
  border: "1px solid #c8d2de",
  background: "#f1f5f9",
  color: "#475569",
  borderRadius: "12px",
  padding: "10px 18px",
  fontWeight: "600",
  cursor: "pointer"
}

const subscriptionSaveBtn = {
  border: "1px solid #0b4f84",
  background: "#0b4f84",
  color: "#ffffff",
  borderRadius: "12px",
  padding: "10px 22px",
  fontWeight: "600",
  cursor: "pointer"
}


